import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from "react";
import {
  Alert,
  Avatar,
  Box,
  Chip,
  Badge,
  Tooltip,
  CircularProgress,
  TextField,
  SpeedDial,
  SpeedDialAction,
  Fab,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

import SendIcon from "@mui/icons-material/Send";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import MovieCreationIcon from "@mui/icons-material/MovieCreation";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RefreshIcon from "@mui/icons-material/Refresh";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";

// Chat sub-components
import DeliverableCard from "../Pages/Dashboard/Campaigns/projectcomponents/smallercomps/deliverablecard";
import SendOfferDialog from "../Pages/Dashboard/Campaigns/projectcomponents/smallercomps/sendoffer";
import AttachmentBubble from "./ChatComponents/AttachmentBubble";
import AlertDialog from "./AlertDialog";
import ChatBubble from "./ChatComponents/ChatBubble";
import ReadReceipt from "./ChatComponents/ReadReceipt";
import ShippingAddressDialog from "./ChatComponents/ShippingAddressDialog";
import SelectAssetDialog from "./tools/selectfile";
import SelectCampaignDialog from "./tools/selectcampaigndialog";
import SelectCreatorDialog from "./tools/selectcreatordialog";
import CreatorUploadFileDialog from "../CreatorUser/CreatorPages/Dashboard/creatorUtils/uploadfile";
import OfferDialog from "../Pages/Dashboard/Campaigns/projectcomponents/smallercomps/offerdialog";
import PdfSigningDialog from "./ChatComponents/pdfsigning";
import CreatorInvoiceDialog from "../CreatorUser/CreatorPages/Dashboard/misc/creatorinvoicepopup";
import CampaignVideoEditor from "./CampaignVideoEditor";
import EditVideoLinksDialog from "./ChatComponents/EditVideoLinksDialog";
import ReplyPreview from "./ChatComponents/ReplyPreview";

// Hooks & Utilities
import API from "../API";
import { useMutation, useQuery, useQueryClient } from "react-query";
import useAuth from "../Hooks/use-auth";
import { replaceConsoleError } from "../Utils/toastNotifications";
import { useCreatorAuth } from "../Hooks/creator-use-auth";
import useAlertDialog from "./useAlertDialog";
import useIsDesktop from "../Hooks/useIsDesktop";
import { getFileType, isMessageMine, getTypingText } from "../Utils/constants";
import { useSocket } from "../Context/SocketContext";
import useMentions from "./ChatComponents/useMentions";
import MentionSuggestions from "./ChatComponents/MentionSuggestions";

/* ------------------------------------------------------------------ */
/*  Constants & Helpers                                               */
/* ------------------------------------------------------------------ */
const MAX_MESSAGES_WITHOUT_RESPONSE = 5;
const SCROLL_TIMEOUT = 100;
const SNACKBAR_DURATION = 6000;
const MAX_MESSAGE_ROWS = 3;
const POLLING_INTERVAL = 10000; // Fallback polling when socket disconnected (10 seconds)
const NAV_BAR_HEIGHT = 86;

// Show polling indicator only every N polls to reduce UI noise
const SHOW_POLLING_INDICATOR_EVERY = 6; // Show visual feedback every 60 seconds when polling

// Generate temporary ID for optimistic updates
const generateTempId = () =>
  `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const getIcon = (attachmentType) => {
  switch (attachmentType) {
    case "campaign":
      return <CampaignOutlinedIcon color="primary" />;
    case "address":
      return <LocalShippingIcon color="primary" />;
    default:
      return undefined;
  }
};

const paymentReady = (video) =>
  !!video.liveLink &&
  !!video.boostCode &&
  video.crossposts.every((c) => !!c.liveLink && !!c.boostCode) &&
  !video.payoutComplete;

const ProcessStep = ({ text }) => (
  <ListItem>
    <ListItemIcon>
      <CheckCircleIcon color="primary" />
    </ListItemIcon>
    <ListItemText primary={text} />
  </ListItem>
);

/* ------------------------------------------------------------------ */
/*  Enhanced ChatScreen with Improved Visual Feedback                */
/* ------------------------------------------------------------------ */
const ChatScreen = forwardRef(function ChatScreen(
  {
    children,
    conversationId,
    partnershipId,
    creatorId,
    creator, // Creator object with display information
    onDeliverableClick = undefined,
    onDeliverablesUpdate = () => {},
    showWarning = true,
    specificReaderId = null,
    chatSpacing = 1,
    margin = 2,
    sx = {},
    conversationTheme,
  },
  ref,
) {
  const theme = useTheme();
  const isPartnershipClean = conversationTheme?.layout === "partnership-clean";
  const { getCurrrentUser } = useAuth();
  const userInfo = getCurrrentUser();
  const { creatorToken } = useCreatorAuth();
  const queryClient = useQueryClient();
  const isDesktop = useIsDesktop();
  const isDesktopPartnershipClean = isPartnershipClean && isDesktop;
  const { dialogState, openDialog, closeDialog } = useAlertDialog();
  const listRef = useRef(null);
  const lastMessageId = useRef(null);
  const pollingTimeoutRef = useRef(null);
  const isPollingRef = useRef(false);
  const pollCountRef = useRef(0);

  // WebSocket integration
  const {
    isConnected: socketConnected,
    joinConversation,
    leaveConversation,
    sendMessage: socketSendMessage,
    sendTypingStatus,
    markAsRead: socketMarkAsRead,
    addReaction: socketAddReaction,
    removeReaction: socketRemoveReaction,
    typingUsers,
    getTypingUsers,
    onlineUsers,
    getOnlineUsers,
    isUserOnline,
    roomPresence,
    getRoomOnlineUsers,
  } = useSocket();

  /* ------------------------------------------------------------------ */
  /*  State Management                                                  */
  /* ------------------------------------------------------------------ */
  const [message, setMessage] = useState("");
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [chatError, setChatError] = useState(null);
  const [currentMessage, setCurrentMessage] = useState(null);
  const [showMessageCapAlert, setShowMessageCapAlert] = useState(false);
  const [isManualRefresh, setIsManualRefresh] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const [showPollingIndicator, setShowPollingIndicator] = useState(false);

  // Dialog states
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [campaignDialogOpen, setCampaignDialogOpen] = useState(false);
  const [creatorDialogOpen, setCreatorDialogOpen] = useState(false);
  const [creatorUploadDialogOpen, setCreatorUploadDialogOpen] = useState(false);
  const [creatorShippingAddress, setCreatorShippingAddress] = useState(false);
  const [openVideo, setOpenVideo] = useState(false);
  const [openVideoLink, setOpenVideoLink] = useState(false);
  const [videoLinkId, setVideoLinkId] = useState(null);
  const [sendOfferOpen, setSendOfferOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [viewOfferOpen, setViewOfferOpen] = useState(false);
  const [pdfSigningDialogOpen, setPdfSigningDialogOpen] = useState(false);
  const [fileForSigning, setFileForSigning] = useState(null);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [reactionLoading, setReactionLoading] = useState(new Set());
  const [snack, setSnack] = useState({ msg: "", sev: "info" });
  const [replyToMessage, setReplyToMessage] = useState(null);
  const [isQuoting, setIsQuoting] = useState(false);

  // Optimistic messages for REST fallback
  const [optimisticMessages, setOptimisticMessages] = useState([]);

  // Typing indicator state
  const typingTimeoutRef = useRef(null);
  const isTypingRef = useRef(false);

  // Get typing users for this conversation
  const currentTypingUsers = useMemo(() => {
    return getTypingUsers(conversationId) || [];
  }, [getTypingUsers, conversationId, typingUsers]);

  /* ------------------------------------------------------------------ */
  /*  Derived Variables                                                 */
  /* ------------------------------------------------------------------ */
  const userId =
    specificReaderId ||
    userInfo?.id ||
    creatorToken?.creator_user?.username ||
    null;
  const userCompany = userInfo?.company_name;
  const isTcc = userCompany === "TCC";
  const isCreator = !!specificReaderId || (!!creatorToken && !userInfo);

  /* ------------------------------------------------------------------ */
  /*  Enhanced Data Fetching                                            */
  /* ------------------------------------------------------------------ */

  // Metadata query (less frequent updates)
  const {
    data: metadata,
    isLoading: metadataLoading,
    refetch: metadataRefetch,
    error: metadataError,
  } = useQuery(
    ["messages", "metadata", conversationId],
    () => {
      return API.conversations.getMetadata(
        conversationId,
        specificReaderId !== null ? { reader_id: specificReaderId } : undefined,
      );
    },
    {
      enabled: !!conversationId,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5, // 5 minutes
      cacheTime: 1000 * 60 * 30, // 30 minutes
    },
  );
  const conversationColor =
    metadata?.conversation?.theme?.color ||
    conversationTheme?.color ||
    theme.palette.primary.main;
  const hasTypedDraft = message.trim().length > 0;
  const showCompactMoneyAction = !isPartnershipClean || !hasTypedDraft;

  // Messages query with optimized polling
  const {
    data: conversation,
    isLoading: messagesLoading,
    isFetching: messagesRefetching,
    refetch: messagesRefetch,
    error: messagesError,
    dataUpdatedAt: lastFetchTime,
  } = useQuery(
    ["messages", conversationId],
    () => {
      return API.conversations.getMessages(
        conversationId,
        specificReaderId !== null ? { reader_id: specificReaderId } : undefined,
      );
    },
    {
      enabled: !!metadata,
      // Disable refetch on window focus when socket is connected (socket handles real-time updates)
      refetchOnWindowFocus: !socketConnected,
      staleTime: 1000 * 30, // 30 seconds
      cacheTime: 1000 * 60 * 10, // 10 minutes
      onError: (err) => {
        replaceConsoleError("Messages fetch error", err);
        setChatError("Failed to load messages");
      },
      onSuccess: (data) => {
        setChatError(null);
        setLastUpdateTime(new Date());
      },
    },
  );

  // @mention support - use same merging logic as chatextra.js
  const mentionParticipants = useMemo(() => {
    // Try all possible sources
    const metadataParticipants = metadata?.participants || [];
    const conversationParticipants = conversation?.participants || [];
    const globalParticipants =
      metadata?.conversation?.global_participants || [];

    const participantMap = new Map();

    // Add metadata participants first
    metadataParticipants.forEach((p) => {
      const key = String(p.global_user_id || p.id);
      participantMap.set(key, {
        ...p,
        isGlobalParticipant: false,
        isRegularParticipant: true,
        global_user_id: p.global_user_id || p.id,
      });
    });

    // Add conversation participants
    conversationParticipants.forEach((p) => {
      const key = String(p.global_user_id || p.id);
      if (!participantMap.has(key)) {
        participantMap.set(key, {
          ...p,
          isGlobalParticipant: false,
          isRegularParticipant: true,
          global_user_id: p.global_user_id || p.id,
        });
      }
    });

    // Merge global participants
    globalParticipants.forEach((gp) => {
      const key = String(gp.id);
      const existing = participantMap.get(key);

      if (existing) {
        participantMap.set(key, {
          ...existing,
          ...gp,
          isGlobalParticipant: true,
          global_user_id: gp.id,
        });
      } else {
        participantMap.set(key, {
          ...gp,
          id: gp.id,
          username: gp.username,
          pfp: gp.avatar || gp.pfp,
          is_creator: gp.user_type === "creator",
          isGlobalParticipant: true,
          isRegularParticipant: false,
          global_user_id: gp.id,
        });
      }
    });

    return Array.from(participantMap.values());
  }, [
    metadata?.participants,
    conversation?.participants,
    metadata?.conversation?.global_participants,
  ]);
  const mentions = useMentions(mentionParticipants, userId);

  const startPolling = useCallback(() => {
    // Don't poll if socket is connected - real-time updates via WebSocket
    if (isPollingRef.current || !conversationId || socketConnected) return;

    isPollingRef.current = true;
    pollCountRef.current = 0;

    const poll = async () => {
      // Stop polling if socket became connected
      if (socketConnected) {
        stopPolling();
        return;
      }

      try {
        if (document.hidden || !conversationId) {
          // Stop polling when tab is not visible
          pollingTimeoutRef.current = setTimeout(poll, POLLING_INTERVAL * 2);
          return;
        }

        pollCountRef.current++;

        // Only show visual indicator occasionally for automatic polling
        const shouldShowIndicator =
          pollCountRef.current % SHOW_POLLING_INDICATOR_EVERY === 0;
        if (shouldShowIndicator) {
          setShowPollingIndicator(true);
        }

        await messagesRefetch();

        // Hide indicator after a short delay
        if (shouldShowIndicator) {
          setTimeout(() => setShowPollingIndicator(false), 1000);
        }

        pollingTimeoutRef.current = setTimeout(poll, POLLING_INTERVAL);
      } catch (error) {
        replaceConsoleError("Polling error", error);
        setShowPollingIndicator(false);
        // Exponential backoff on error
        pollingTimeoutRef.current = setTimeout(poll, POLLING_INTERVAL * 2);
      }
    };

    pollingTimeoutRef.current = setTimeout(poll, POLLING_INTERVAL);
  }, [conversationId, messagesRefetch, socketConnected]);

  const stopPolling = useCallback(() => {
    isPollingRef.current = false;
    setShowPollingIndicator(false);
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
  }, []);

  // WebSocket room management - join conversation and mark messages as read
  useEffect(() => {
    if (!conversationId) return;

    if (socketConnected) {
      joinConversation(conversationId);
      socketMarkAsRead(conversationId);
    }

    return () => {
      if (socketConnected) {
        leaveConversation(conversationId);
      }
    };
  }, [
    conversationId,
    socketConnected,
    joinConversation,
    leaveConversation,
    socketMarkAsRead,
  ]);

  // Start/stop polling based on socket connection status and visibility
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPolling();
      } else if (conversationId && !socketConnected) {
        startPolling();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    if (conversationId && !document.hidden && !socketConnected) {
      startPolling();
    } else if (socketConnected) {
      stopPolling();
    }

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      stopPolling();
    };
  }, [conversationId, startPolling, stopPolling, socketConnected]);

  /* ------------------------------------------------------------------ */
  /*  Enhanced Message Processing                                       */
  /* ------------------------------------------------------------------ */
  const allMessages = useMemo(() => {
    const serverMessages = conversation?.messages || [];
    // Combine server messages with optimistic messages, filtering out any optimistic
    // messages that have been confirmed (matching by client_temp_id)
    const serverTempIds = new Set(
      serverMessages
        .filter((m) => m.client_temp_id)
        .map((m) => m.client_temp_id),
    );
    const pendingOptimistic = optimisticMessages.filter(
      (m) => !serverTempIds.has(m.client_temp_id),
    );
    return [...serverMessages, ...pendingOptimistic];
  }, [conversation?.messages, optimisticMessages]);

  /* ------------------------------------------------------------------ */
  /*  Online Status (per-room presence)                                 */
  /* ------------------------------------------------------------------ */
  const roomOnline = useMemo(
    () => getRoomOnlineUsers(conversationId),
    [getRoomOnlineUsers, conversationId, roomPresence],
  );
  const onlineUsersCount = roomOnline.online_count || 0;
  const onlineUsersList = roomOnline.online_users || [];

  const deliverables = useMemo(
    () => allMessages.filter((m) => m.deliverable),
    [allMessages],
  );

  const deliverableNotice = useMemo(() => {
    if (deliverables.length === 0) return null;

    const statuses = deliverables.map((d) =>
      (d.deliverable.status || "").toString().toLowerCase(),
    );

    if (deliverables.length > 1) {
      return "There are multiple deliverables in this chat, please ensure they get done ASAP!";
    }

    if (statuses.some((s) => s === "sent" || s === "pending")) {
      return "Creator needs to accept this deliverable to make an active partnership.";
    }

    if (statuses.some((s) => s === "accept" || s === "accepted")) {
      return "Reminder: the work needs to be marked as completed for payments to be issued.";
    }

    return null;
  }, [deliverables]);

  /* ------------------------------------------------------------------ */
  /*  Enhanced Mutations                                                */
  /* ------------------------------------------------------------------ */
  const { mutate: deleteMessage, isLoading: deleteMessageLoading } =
    useMutation((msgId) => API.conversations.deleteMessage(msgId), {
      onSuccess: () => {
        messagesRefetch();
      },
      onError: (error) => {
        replaceConsoleError("Delete message error", error);
        openDialog(
          "Error",
          `Error while deleting message: ${error?.response?.data?.error || error.message}`,
        );
      },
    });

  // Enhanced send message with optimistic updates (REST fallback)
  const { mutate: sendMessage, isLoading: sendMessageLoading } = useMutation(
    ({ id, input }) => API.conversations.sendMessage(id, input),
    {
      onMutate: async ({ input }) => {
        // Create optimistic message with reply data
        const tempId = generateTempId();
        const optimisticMessage = {
          id: tempId,
          client_temp_id: tempId,
          message: input.message || "Attachment",
          sender_id: isCreator ? null : userId,
          creator_id: isCreator ? userId : null,
          created_at: new Date().toISOString(),
          is_pending: true,
          isOptimistic: true,
          attachments: input.attachment
            ? {
                attachment_type: input.attachment.type,
                data: input.attachment.data,
                label: input.attachment.label,
              }
            : null,
          read_receipts: {},
          // Reply data
          reply_to_message_id: input.reply_to_message_id || null,
          is_quote: input.is_quote || false,
          reply_to: replyToMessage
            ? {
                id: replyToMessage.id,
                message: replyToMessage.message,
                sender_name:
                  replyToMessage.sender?.first_name ||
                  replyToMessage.creator_id ||
                  "User",
                created_at: replyToMessage.created_at,
              }
            : null,
        };

        setOptimisticMessages((prev) => [...prev, optimisticMessage]);

        // Clear input and reply state immediately
        setMessage("");
        setSelectedAttachment(null);
        setReplyToMessage(null);
        setIsQuoting(false);

        setTimeout(() => {
          if (listRef.current) {
            listRef.current.scrollTo({
              top: listRef.current.scrollHeight,
              behavior: "smooth",
            });
          }
        }, 50);

        return { tempId };
      },
      onSuccess: (data, variables, context) => {
        if (context?.tempId) {
          setOptimisticMessages((prev) =>
            prev.filter((msg) => msg.client_temp_id !== context.tempId),
          );
        }
        messagesRefetch();
      },
      onError: (error, variables, context) => {
        replaceConsoleError("Error sending message", error);

        if (context?.tempId) {
          // Mark as failed instead of removing
          setOptimisticMessages((prev) =>
            prev.map((msg) =>
              msg.client_temp_id === context.tempId
                ? { ...msg, is_pending: false, is_failed: true }
                : msg,
            ),
          );
        }

        // Restore the input values including reply state
        setMessage(variables.input.message || "");
        if (variables.input.attachment) {
          setSelectedAttachment(variables.input.attachment);
        }
        if (variables.input.reply_to_message_id) {
          setReplyToMessage(replyToMessage);
          setIsQuoting(variables.input.is_quote || false);
        }

        handleSendMessageError(error);
      },
    },
  );

  // ✅ FIXED: Use mutateAsync for proper async handling
  const { mutateAsync: addReactionAsync } = useMutation(
    ({ messageId, reactionType }) =>
      API.conversations.addReaction(messageId, reactionType),
    {
      onSuccess: () => {
        messagesRefetch();
      },
      onError: (error) => {
        replaceConsoleError("Error adding reaction", error);
        setSnack({ msg: "Failed to add reaction", sev: "error" });
      },
    },
  );

  const { mutateAsync: removeReactionAsync } = useMutation(
    ({ messageId, reactionType }) =>
      API.conversations.removeReaction(messageId, reactionType),
    {
      onSuccess: () => {
        messagesRefetch();
      },
      onError: (error) => {
        replaceConsoleError("Error removing reaction", error);
        setSnack({ msg: "Failed to remove reaction", sev: "error" });
      },
    },
  );

  // ✅ FIXED: Properly handle async mutations with socket fallback
  const handleReactionAdd = useCallback(
    async (messageId, reactionType) => {
      if (reactionLoading.has(messageId)) return;

      setReactionLoading((prev) => new Set(prev.add(messageId)));

      try {
        // Use socket when connected, REST API as fallback
        if (socketConnected && conversationId) {
          socketAddReaction(conversationId, messageId, reactionType);
          setSnack({ msg: "Reaction added!", sev: "success" });
        } else {
          await addReactionAsync({ messageId, reactionType });
          setSnack({ msg: "Reaction added!", sev: "success" });
        }
      } catch (error) {
        console.error("Reaction add error:", error);
        setSnack({ msg: "Failed to add reaction", sev: "error" });
      } finally {
        setReactionLoading((prev) => {
          const newSet = new Set(prev);
          newSet.delete(messageId);
          return newSet;
        });
      }
    },
    [
      addReactionAsync,
      reactionLoading,
      socketConnected,
      conversationId,
      socketAddReaction,
    ],
  );

  const handleReactionRemove = useCallback(
    async (messageId, reactionType) => {
      if (reactionLoading.has(messageId)) return;

      setReactionLoading((prev) => new Set(prev.add(messageId)));

      try {
        if (socketConnected && conversationId) {
          socketRemoveReaction(conversationId, messageId, reactionType);
          setSnack({ msg: "Reaction removed!", sev: "success" });
        } else {
          await removeReactionAsync({ messageId, reactionType });
          setSnack({ msg: "Reaction removed!", sev: "success" });
        }
      } catch (error) {
        console.error("Reaction remove error:", error);
        setSnack({ msg: "Failed to remove reaction", sev: "error" });
      } finally {
        setReactionLoading((prev) => {
          const newSet = new Set(prev);
          newSet.delete(messageId);
          return newSet;
        });
      }
    },
    [
      removeReactionAsync,
      reactionLoading,
      socketConnected,
      conversationId,
      socketRemoveReaction,
    ],
  );

  const handleReply = useCallback((message) => {
    setReplyToMessage(message);
    setIsQuoting(false);
    // Focus the text input
    setTimeout(() => {
      const input = document.querySelector('textarea[name="chat-message"]');
      if (input) input.focus();
    }, 100);
  }, []);

  const handleQuote = useCallback((message) => {
    setReplyToMessage(message);
    setIsQuoting(true);
    // Focus the text input
    setTimeout(() => {
      const input = document.querySelector('textarea[name="chat-message"]');
      if (input) input.focus();
    }, 100);
  }, []);

  const handleCancelReply = useCallback(() => {
    setReplyToMessage(null);
    setIsQuoting(false);
  }, []);

  // Handle typing indicator
  const handleTyping = useCallback(
    (newMessage) => {
      setMessage(newMessage);

      if (!socketConnected || !conversationId) return;

      // Send typing status via socket
      if (newMessage.trim() && !isTypingRef.current) {
        isTypingRef.current = true;
        sendTypingStatus(conversationId, true);
      }

      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      // Set timeout to stop typing indicator after 2 seconds of inactivity
      typingTimeoutRef.current = setTimeout(() => {
        if (isTypingRef.current) {
          isTypingRef.current = false;
          sendTypingStatus(conversationId, false);
        }
      }, 2000);
    },
    [socketConnected, conversationId, sendTypingStatus],
  );

  // Cleanup typing timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      // Send stop typing on unmount
      if (isTypingRef.current && socketConnected && conversationId) {
        sendTypingStatus(conversationId, false);
      }
    };
  }, [socketConnected, conversationId, sendTypingStatus]);

  // Scroll to message function
  const scrollToMessage = useCallback(
    (messageId) => {
      if (!messageId) return;

      const messageElement = document.getElementById(`msg-${messageId}`);
      if (messageElement) {
        messageElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });

        // Add a brief highlight effect to make it easier to spot
        messageElement.style.transition = "background-color 0.3s ease";
        messageElement.style.backgroundColor = alpha(
          theme.palette.primary.main,
          0.1,
        );

        setTimeout(() => {
          messageElement.style.backgroundColor = "transparent";
        }, 2000);
      } else {
        // If message not found in current view, it might be further up
        // Scroll to top and try again after a brief delay
        if (listRef.current) {
          listRef.current.scrollTo({ top: 0, behavior: "smooth" });
          setTimeout(() => {
            const retryElement = document.getElementById(`msg-${messageId}`);
            if (retryElement) {
              retryElement.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            }
          }, 500);
        }
      }
    },
    [theme.palette.primary.main],
  );

  // Reaction change handler
  const handleReactionChange = useCallback((data) => {
    setSnack({
      msg: `${data.reaction ? "Reaction added" : "Reaction removed"}!`,
      sev: "success",
    });
  }, []);

  // Payment mutations

  const { mutate: bubbleCompleteVideo, isLoading: bubblePaying } = useMutation(
    (videoId) => {
      if (
        !metadata?.conversation?.campaign_id ||
        !metadata?.conversation?.creator_id
      ) {
        throw new Error("Missing campaign or creator ID");
      }
      return API.campaigns.completeVideo(metadata.conversation.campaign_id, {
        creator: metadata.conversation.creator_id,
        video: videoId,
      });
    },
    {
      onSuccess: () => {
        openDialog("Paid", "Payout completed.");
        messagesRefetch();
      },
      onError: (err) =>
        openDialog("Error", err.response?.data?.error || err.message),
    },
  );

  const { mutate: bubbleRequestPayment, isLoading: bubbleRequesting } =
    useMutation(
      (videoId) =>
        API.conversations.sendMessage(conversationId, {
          message: `Creator requested payment for Video ${videoId}`,
        }),
      {
        onSuccess: () => {
          openDialog("Sent", "Payment request sent.");
          messagesRefetch();
        },
        onError: (err) =>
          openDialog("Error", err.response?.data?.error || err.message),
      },
    );

  // Enhanced Event Handlers

  const userMessagesCount = useMemo(() => {
    if (!allMessages || allMessages.length === 0) return 0;

    const userMessages = allMessages.filter((m) => isMessageMine(userId, m));
    const otherMessages = allMessages.filter((m) => !isMessageMine(userId, m));

    if (otherMessages.length > 0) {
      // Sort by time to ensure we get the actual last message from other party
      const sortedOtherMessages = [...otherMessages].sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
      const lastOtherMsgTime = new Date(
        sortedOtherMessages[0].created_at,
      ).getTime();

      // Count only user messages sent AFTER the last response from other party
      return userMessages.filter(
        (m) => new Date(m.created_at).getTime() > lastOtherMsgTime,
      ).length;
    }

    return userMessages.length;
  }, [allMessages, userId]);

  // Enhanced auto-scroll with better performance
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (listRef.current && allMessages.length > 0) {
        const latestMessage = allMessages[allMessages.length - 1];
        if (latestMessage.id !== lastMessageId.current) {
          listRef.current.scrollTo({
            top: listRef.current.scrollHeight,
            behavior: "smooth",
          });
          lastMessageId.current = latestMessage.id;
        }
      }
    }, SCROLL_TIMEOUT);
    return () => clearTimeout(timeoutId);
  }, [allMessages]);

  const handleManualRefresh = useCallback(async () => {
    setIsManualRefresh(true);
    try {
      await messagesRefetch();
    } finally {
      setIsManualRefresh(false);
    }
  }, [messagesRefetch]);

  const isMessageAllowed = useCallback((text) => {
    const prohibitedKeywords = [
      "text me",
      "contact me",
      "contact",
      "@gmail.com",
      "whatsapp",
      "convenient contact",
    ];
    const phoneRegex = /(?:\d[\s-]?){10,}/;
    const urlRegex = /https?:\/\/[^\s]+/g;
    const textNoUrls = text.replace(urlRegex, "");
    const lowerText = textNoUrls.toLowerCase();

    if (prohibitedKeywords.some((k) => lowerText.includes(k))) {
      return false;
    }
    return !phoneRegex.test(textNoUrls);
  }, []);

  const handleSendMessage = useCallback(async () => {
    if (userMessagesCount >= MAX_MESSAGES_WITHOUT_RESPONSE && !isTcc) {
      setShowMessageCapAlert(true);
      return;
    }

    if (!isMessageAllowed(message)) {
      openDialog(
        "Error",
        "Your message contains prohibited content. Please refrain from sharing personal contact information.",
      );
      return;
    }

    if ((!message || message.trim() === "") && !selectedAttachment) {
      openDialog("Error", "Can't send an empty message");
      return;
    }

    if (!conversationId) {
      openDialog(
        "Error",
        "No valid conversation. Please contact support if this persists.",
      );
      return;
    }

    // Stop typing indicator
    if (isTypingRef.current) {
      isTypingRef.current = false;
      sendTypingStatus(conversationId, false);
    }

    const inputData = {
      message,
      ...(specificReaderId && { reader_id: specificReaderId }),
      ...(selectedAttachment && { attachment: selectedAttachment }),
      ...(replyToMessage && {
        reply_to_message_id: replyToMessage.id,
        is_quote: isQuoting,
      }),
    };

    // Use socket when connected, REST API as fallback
    if (socketConnected) {
      // Create optimistic message for instant feedback
      const tempId = generateTempId();
      const optimisticMessage = {
        id: tempId, // Temporary ID, will be replaced by server
        client_temp_id: tempId,
        message: message || "Attachment",
        sender_id: isCreator ? null : userId,
        creator_id: isCreator ? userId : null,
        created_at: new Date().toISOString(),
        isOptimistic: true,
        is_pending: true, // Show single tick (pending state)
        attachments: selectedAttachment
          ? {
              attachment_type: selectedAttachment.type,
              data: selectedAttachment.data,
              label: selectedAttachment.label,
            }
          : null,
        read_receipts: {},
        reply_to_message_id: replyToMessage?.id || null,
        is_quote: isQuoting,
        reply_to: replyToMessage
          ? {
              id: replyToMessage.id,
              message: replyToMessage.message,
              sender_name:
                replyToMessage.sender?.first_name ||
                replyToMessage.creator_id ||
                "User",
              created_at: replyToMessage.created_at,
            }
          : null,
      };

      // ✅ Unified state: update query cache directly instead of local state
      queryClient.setQueryData(["messages", conversationId], (old) => ({
        ...old,
        messages: [...(old?.messages || []), optimisticMessage],
      }));

      // Clear input immediately
      setMessage("");
      setSelectedAttachment(null);
      setReplyToMessage(null);
      setIsQuoting(false);

      // Scroll to bottom immediately
      setTimeout(() => {
        if (listRef.current) {
          listRef.current.scrollTo({
            top: listRef.current.scrollHeight,
            behavior: "auto", // Changed: Faster feel than "smooth"
          });
        }
      }, 0);

      // Send via socket
      socketSendMessage({
        conversation_id: conversationId,
        message: message,
        attachment_id: selectedAttachment?.data?.id || null,
        reply_to_message_id: replyToMessage?.id || null,
        is_quote: isQuoting,
        client_temp_id: tempId,
      });
    } else {
      // Fallback to REST API
      sendMessage({ id: conversationId, input: inputData });
    }
  }, [
    message,
    selectedAttachment,
    conversationId,
    specificReaderId,
    userMessagesCount,
    isTcc,
    isMessageAllowed,
    openDialog,
    sendMessage,
    replyToMessage,
    isQuoting,
    socketConnected,
    socketSendMessage,
    sendTypingStatus,
    isCreator,
    userId,
    queryClient,
  ]);

  const handleSendMessageError = useCallback(
    (error) => {
      if (error?.response?.data?.error === "forbidden") {
        openDialog(
          "Error",
          "This message was flagged as containing prohibited content. Please refrain from sharing personal contact information.",
          closeDialog,
          closeDialog,
          "Ok",
          null,
        );
        return;
      }
      openDialog(
        "Error",
        `Error while sending message: ${error?.response?.data?.error || error.message}`,
      );
    },
    [openDialog, closeDialog],
  );

  /* ------------------------------------------------------------------ */
  /*  Attachment Handlers                                               */
  /* ------------------------------------------------------------------ */
  const handleVideoLink = (videoId) => {
    setVideoLinkId(videoId);
    setOpenVideoLink(true);
  };

  const handleFileSelect = useCallback((file) => {
    const isImage = getFileType(file.filename) === "image";
    setSelectedAttachment({
      type: "file",
      label: file.filename,
      data: file,
      preview: isImage ? file.file_url : undefined,
    });
    setUploadDialogOpen(false);
    setMessage("");
  }, []);

  const handleCreatorSelect = useCallback((creator) => {
    setSelectedAttachment({
      type: "creator",
      label: `${creator.display_name} Profile`,
      preview: creator.pfphref,
      data: { creator: creator.display_name },
    });
    setCreatorDialogOpen(false);
    setMessage("");
  }, []);

  const handleCampaignSelect = useCallback((campaign) => {
    setSelectedAttachment({
      type: "campaign",
      label: campaign.name,
      data: { id: campaign.id },
    });
    setCampaignDialogOpen(false);
    setMessage("");
  }, []);

  const handleShippingSelect = useCallback((form) => {
    setCreatorShippingAddress(false);
    if (!form) return;
    setSelectedAttachment({
      type: "address",
      label: form.address + "...",
      data: { data: form },
    });
    setMessage("");
  }, []);

  const handleCreatorFile = useCallback(
    (file) => {
      const baseInput = specificReaderId ? { reader_id: specificReaderId } : {};
      if (file.status === "uploaded_to_firebase") {
        sendMessage({
          id: conversationId,
          input: {
            ...baseInput,
            message: "",
            attachment: { type: "file", data: { id: file.file_id } },
          },
        });
      } else {
        sendMessage({
          id: conversationId,
          input: {
            ...baseInput,
            message: `Large attachments folder: ${file.redirect_url}`,
          },
        });
        openDialog(
          "Alert",
          "A Google Drive folder was created for this upload. Opening in a new window...",
          () => window.open(file.redirect_url, "_blank"),
          () => window.open(file.redirect_url, "_blank"),
        );
      }
      setCreatorUploadDialogOpen(false);
      setMessage("");
    },
    [conversationId, specificReaderId, openDialog, sendMessage],
  );

  const handleCreatorUploadError = useCallback(
    (err) => {
      replaceConsoleError("Creator upload error", err);
      setCreatorUploadDialogOpen(false);
      openDialog(
        "Error",
        `Error while sending message: ${err?.response?.data?.error || err.message}`,
      );
    },
    [openDialog],
  );

  const handleOfferSent = () => {
    setSendOfferOpen(false);
    messagesRefetch();
  };

  const handleViewOffer = (offer) => {
    if (onDeliverableClick) {
      onDeliverableClick(offer);
    } else {
      setSelectedOffer(offer);
      setViewOfferOpen(true);
    }
  };

  const handleDeleteMessage = useCallback(
    (messageId) => {
      if (isTcc) {
        openDialog(
          "Warning",
          "Are you sure you want to delete this message?",
          () => {
            closeDialog();
            deleteMessage(messageId);
          },
          closeDialog,
          "Yes",
          "No",
        );
      }
    },
    [isTcc, openDialog, closeDialog, deleteMessage],
  );

  const isPdfFile = (file) => {
    const fileType = file.file_type ? file.file_type.toLowerCase() : "";
    const filename = file.filename ? file.filename.toLowerCase() : "";
    return (
      fileType === "pdf" || fileType === "contract" || filename.endsWith(".pdf")
    );
  };

  const handleEditFile = (file) => {
    setFileForSigning(file);
    setPdfSigningDialogOpen(true);
  };

  const handleInvoiceCreated = useCallback(
    async (newInvoice) => {
      setInvoiceDialogOpen(false);
      const baseInput = specificReaderId ? { reader_id: specificReaderId } : {};
      try {
        await sendMessage({
          id: conversationId,
          input: {
            ...baseInput,
            message: "",
            attachment: {
              type: "invoice",
              data: {
                invoice_id: newInvoice.id,
                invoice_data: newInvoice,
              },
            },
          },
        });
      } catch (error) {
        replaceConsoleError("Error sending invoice message", error);
        openDialog(
          "Error",
          `Failed to send invoice: ${error?.response?.data?.error || error.message}`,
        );
      }
    },
    [conversationId, specificReaderId, sendMessage, openDialog],
  );

  //  Imperative Handle

  useImperativeHandle(ref, () => ({
    scrollToFirstDeliverable() {
      if (deliverables.length === 0) return;
      const el = document.getElementById(`deliverable-${deliverables[0].id}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      else listRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    },
    scrollToMessage,
  }));

  useEffect(() => {
    onDeliverablesUpdate(deliverables);
  }, [deliverables, onDeliverablesUpdate]);

  //  Enhanced Render with Better Loading States

  return (
    <>
      <Box
        id="chat-holder"
        sx={{
          ...sx,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "100%",
          bgcolor: isPartnershipClean
            ? "#f4f7fb"
            : theme.palette.mode === "dark"
              ? "background.default"
              : "#efe7dd",
          pb: (theme) =>
            isPartnershipClean || window.location.pathname.includes("/con")
              ? 0
              : `${NAV_BAR_HEIGHT}px`,
        }}
      >
        {/* Messages Container */}
        <Box
          display="flex"
          flexDirection="column"
          sx={{
            paddingInline: margin,
            paddingBlockStart: 2,
            minHeight: "300px",
            minWidth: "0px",
            width: "100%",
            overflowY: "auto",
            overflowX: "clip",
            flex: 1,
            gap: chatSpacing,
            backgroundColor: isPartnershipClean
              ? "#f4f7fb"
              : theme.palette.mode === "dark"
                ? alpha(theme.palette.background.default, 0.8)
                : "#efe7dd",
            backgroundImage: isPartnershipClean
              ? "none"
              : theme.palette.mode === "dark"
                ? "none"
                : `radial-gradient(${alpha(theme.palette.common.black, 0.08)} 0.8px, transparent 0.8px)`,
            backgroundSize: isPartnershipClean ? "auto" : "18px 18px",
            // Enhanced scrollbar styling
            "&::-webkit-scrollbar": {
              width: 6,
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: alpha(theme.palette.text.primary, 0.2),
              borderRadius: 3,
            },
          }}
          ref={listRef}
        >
          {/* Warning Banner at the top of chat (non-sticky) */}
          {showWarning && (
            <Box
              sx={{
                px: isPartnershipClean ? 1.5 : 2,
                py: isPartnershipClean ? 0.75 : 1,
                backgroundColor: "#fff3cd",
                border: `1px solid ${alpha("#856404", 0.18)}`,
                borderRadius: isPartnershipClean ? 999 : 1,
                mb: 1,
                alignSelf: isPartnershipClean ? "center" : "stretch",
                width: isPartnershipClean ? { xs: "86%", sm: 430 } : "auto",
                maxWidth: isPartnershipClean ? "min(760px, 86%)" : "none",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "#856404",
                  textAlign: "center",
                  fontSize: "0.75rem",
                  fontWeight: 500,
                }}
              >
                ⚠️{" "}
                {isPartnershipClean
                  ? "Deals and payments must stay on Blitz."
                  : isDesktop
                  ? "Deals and payments must happen within the Blitz platform. Please review Blitz Chat Rules."
                  : "Deals must happen within the Blitz platform."}
              </Typography>
            </Box>
          )}

          {deliverableNotice && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.75,
                backgroundColor: "#f8d7da",
                border: `1px solid ${alpha("#721c24", 0.18)}`,
                color: "#721c24",
                px: isPartnershipClean ? 1.5 : 2,
                py: isPartnershipClean ? 0.6 : 0.5,
                borderRadius: isPartnershipClean ? 999 : 1,
                mb: 1,
                alignSelf: isPartnershipClean ? "center" : "stretch",
                width: isPartnershipClean ? { xs: "86%", sm: 430 } : "auto",
                maxWidth: isPartnershipClean ? "min(760px, 86%)" : "none",
              }}
            >
              <Typography
                sx={{
                  textAlign: "center",
                  fontSize: "0.75rem",
                  fontWeight: 500,
                }}
              >
                {isPartnershipClean
                  ? "Mark work complete to issue payments."
                  : deliverableNotice}
              </Typography>
              <IconButton sx={{ p: 0.35, color: "inherit" }} size="small" onClick={() => setHelpOpen(true)}>
                <HelpOutlineIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          )}

          {/* Enhanced Loading States */}
          {messagesLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {messagesError && (
            <Paper
              sx={{
                p: 2,
                bgcolor: alpha(theme.palette.error.main, 0.1),
                color: "error.main",
                textAlign: "center",
              }}
            >
              <Typography>
                Error loading messages:{" "}
                {messagesError?.response?.data?.error || messagesError.message}
              </Typography>
              <Button
                onClick={handleManualRefresh}
                sx={{ mt: 1 }}
                variant="outlined"
                size="small"
              >
                Retry
              </Button>
            </Paper>
          )}

          {chatError && (
            <Typography color="error" sx={{ textAlign: "center", p: 2 }}>
              {chatError}
            </Typography>
          )}

          {allMessages.length === 0 && !messagesError && !messagesLoading && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 8,
                color: "text.secondary",
                textAlign: "center",
              }}
            >
              <Typography variant="h6" gutterBottom>
                No messages yet
              </Typography>
              <Typography variant="body2">Start the conversation!</Typography>
            </Box>
          )}

          {/* Enhanced Message Rendering with Optimistic Updates */}
          {allMessages.map((msg) => {
            const isUserMessage = isMessageMine(userId, msg);
            const justify = isUserMessage ? "flex-end" : "flex-start";
            const isSimpleMsg = !msg.deliverable && !msg.attachments;

            return (
              <Box
                key={msg.id}
                id={`msg-${msg.id}`}
                sx={{
                  display: "flex",
                  justifyContent: justify,
                  maxWidth: "100%",
                  transition: "all 0.2s",
                  position: "relative",
                }}
              >
                {msg.deliverable && (
                  <Box
                    onClick={() => handleViewOffer(msg.deliverable)}
                    sx={{ cursor: "pointer" }}
                  >
                    <DeliverableCard
                      key={msg.id}
                      deliverable={msg.deliverable}
                      isUserMessage={isUserMessage}
                      onViewOffer={handleViewOffer}
                    />
                  </Box>
                )}

                {msg.attachments && (
                  <AttachmentBubble
                    msg={msg}
                    userId={userId}
                    isTcc={isTcc}
                    handleDeleteMessage={handleDeleteMessage}
                    deleteMessageLoading={deleteMessageLoading}
                    onEditFile={handleEditFile}
                    metadata={metadata}
                    handleVideoLink={handleVideoLink}
                  />
                )}

                {/* Enhanced ChatBubble with reactions and reply click */}
                {isSimpleMsg && (
                  <ChatBubble
                    msg={msg}
                    userId={userId}
                    isTcc={isTcc}
                    handleDeleteMessage={handleDeleteMessage}
                    deleteMessageLoading={deleteMessageLoading}
                    handleReadReceipts={(selectedMsg) =>
                      setCurrentMessage(selectedMsg)
                    }
                    participants={mentionParticipants}
                    conversationId={conversationId}
                    onReactionChange={handleReactionChange}
                    onReactionAdd={handleReactionAdd}
                    onReactionRemove={handleReactionRemove}
                    onReply={handleReply}
                    onQuote={handleQuote}
                    onReplyClick={scrollToMessage}
                    conversationColor={conversationColor}
                    sxMsgText={{ overflowWrap: "anywhere" }}
                  />
                )}
              </Box>
            );
          })}

          {/* Improved real-time indicators - only shown when needed */}
          {isManualRefresh && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                py: 1,
                position: "sticky",
                bottom: 0,
                bgcolor: alpha(theme.palette.background.paper, 0.9),
                backdropFilter: "blur(4px)",
              }}
            >
              <Chip
                size="small"
                label="Refreshing messages..."
                color="primary"
                variant="outlined"
                icon={<RefreshIcon sx={{ fontSize: 16 }} />}
              />
            </Box>
          )}

          {/* Subtle polling indicator - only shown occasionally */}
          {showPollingIndicator && !isManualRefresh && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                py: 0.5,
                position: "sticky",
                bottom: 0,
              }}
            >
              <Chip
                size="small"
                label="Syncing..."
                color="default"
                variant="outlined"
                sx={{
                  fontSize: "0.7rem",
                  height: 24,
                  opacity: 0.7,
                  bgcolor: alpha(theme.palette.background.paper, 0.8),
                  backdropFilter: "blur(4px)",
                }}
              />
            </Box>
          )}
        </Box>

        {/* Enhanced Message Input Area */}
        <Box
          sx={{
            paddingBlock: isDesktopPartnershipClean ? 0.9 : isPartnershipClean ? 0.75 : 1.5,
            paddingInline: margin,
            bgcolor: isPartnershipClean
              ? "#f4f7fb"
              : theme.palette.mode === "dark"
                ? alpha(theme.palette.background.paper, 0.96)
                : "#f7f3ee",
            borderTop: isPartnershipClean
              ? "none"
              : `1px solid ${alpha(theme.palette.divider, 0.7)}`,
            boxShadow: isPartnershipClean
              ? "none"
              : `0 -8px 24px ${alpha(theme.palette.common.black, 0.06)}`,
          }}
        >
          {children}

          {/* Reply Preview */}
          {replyToMessage && (
            <ReplyPreview
              replyToMessage={replyToMessage}
              isQuote={isQuoting}
              onCancel={handleCancelReply}
            />
          )}

          {/* Typing Indicator */}
          {currentTypingUsers.length > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                mb: 1,
                px: 1,
                color: "text.secondary",
                fontSize: "0.75rem",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: "2px",
                  "& > span": {
                    width: 4,
                    height: 4,
                    borderRadius: "50%",
                    backgroundColor: "text.secondary",
                    animation: "typing 1.4s infinite",
                    "&:nth-of-type(2)": { animationDelay: "0.2s" },
                    "&:nth-of-type(3)": { animationDelay: "0.4s" },
                  },
                  "@keyframes typing": {
                    "0%, 60%, 100%": { transform: "translateY(0)" },
                    "30%": { transform: "translateY(-4px)" },
                  },
                }}
              >
                <span />
                <span />
                <span />
              </Box>
              <Typography variant="caption" color="text.secondary">
                {getTypingText(currentTypingUsers)}
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              gap: isDesktopPartnershipClean ? 0.75 : isPartnershipClean ? 0.45 : 1,
              width: "100%",
              ...(isPartnershipClean && {
                alignItems: "center",
                gap: { xs: 0, md: 0.65 },
                px: { xs: 0, md: 0.8 },
                py: 0,
                bgcolor: "transparent",
                border: "none",
                boxShadow: "none",
              }),
            }}
          >
            {isPartnershipClean ? (
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: 0.5, md: 1 },
                  px: { xs: 1, md: 1.2 },
                  py: { xs: 0.45, md: 0.55 },
                  borderRadius: 999,
                  bgcolor: alpha(theme.palette.common.white, 0.98),
                  border: `1px solid ${alpha("#22364d", 0.14)}`,
                  boxShadow: `0 10px 24px ${alpha("#22364d", 0.08)}`,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0, position: "relative" }}>
                  <MentionSuggestions
                    isVisible={mentions.isVisible}
                    participants={mentions.filteredParticipants}
                    selectedIndex={mentions.selectedIndex}
                    onSelect={(p) => {
                      const newMsg = mentions.insertMention(p, message);
                      setMessage(newMsg);
                    }}
                    getDisplayName={mentions.getDisplayName}
                  />
                  <TextField
                    multiline
                    maxRows={MAX_MESSAGE_ROWS}
                    value={message}
                    autoComplete="off"
                    name="chat-message"
                    inputRef={mentions.inputRef}
                    disabled={
                      sendMessageLoading ||
                      messagesLoading ||
                      !conversationId ||
                      !!selectedAttachment
                    }
                    onChange={(e) => {
                      const nextValue = e.target.value;
                      handleTyping(nextValue);
                      if (mentions.isVisible || nextValue.includes("@")) {
                        mentions.onTextChange(
                          nextValue,
                          e.target.selectionStart,
                        );
                      } else {
                        mentions.closeSuggestions();
                      }
                    }}
                    placeholder="Message..."
                    variant="outlined"
                    fullWidth
                    onBlur={() =>
                      setTimeout(() => mentions.closeSuggestions(), 150)
                    }
                    onKeyDown={(e) => {
                      if (mentions.isVisible) {
                        if (e.key === "ArrowDown") {
                          e.preventDefault();
                          mentions.moveDown();
                          return;
                        }
                        if (e.key === "ArrowUp") {
                          e.preventDefault();
                          mentions.moveUp();
                          return;
                        }
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          const selected =
                            mentions.filteredParticipants[mentions.selectedIndex];
                          if (selected) {
                            setMessage(mentions.insertMention(selected, message));
                          }
                          return;
                        }
                        if (e.key === "Escape") {
                          e.preventDefault();
                          mentions.closeSuggestions();
                          return;
                        }
                      }
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    InputProps={{
                      ...(selectedAttachment && {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Chip
                              icon={getIcon(selectedAttachment.type)}
                              avatar={
                                selectedAttachment.preview ? (
                                  <Avatar
                                    alt={selectedAttachment.label}
                                    src={selectedAttachment.preview}
                                  />
                                ) : undefined
                              }
                              label={selectedAttachment.label}
                              sx={{ maxWidth: "14rem" }}
                              onDelete={() => setSelectedAttachment(null)}
                            />
                          </InputAdornment>
                        ),
                      }),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 999,
                        bgcolor: "transparent",
                        alignItems: "center",
                        minHeight: { xs: 36, md: 38 },
                        padding: { xs: "3px 0", md: "4px 0" },
                        boxShadow: "none",
                        "& fieldset": {
                          borderColor: "transparent",
                        },
                        "&:hover fieldset": {
                          borderColor: "transparent",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "transparent",
                        },
                      },
                      "& .MuiOutlinedInput-input": {
                        py: 0,
                        px: 0.25,
                        fontSize: { xs: "0.95rem", md: "0.98rem" },
                      },
                      "& .MuiInputBase-input::placeholder": {
                        color: alpha("#22364d", 0.38),
                        opacity: 1,
                      },
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 0.2, md: 0.35 },
                    pl: { xs: 0.45, md: 1 },
                    borderLeft: `1px solid ${alpha("#22364d", 0.1)}`,
                    flexShrink: 0,
                  }}
                >
                  {!isCreator ? (
                    <Box sx={{ width: { xs: 32, md: 36 }, height: { xs: 32, md: 36 }, position: "relative" }}>
                      <SpeedDial
                        ariaLabel="Attachment Menu"
                        icon={<AttachFileIcon />}
                        direction="up"
                        sx={{
                          position: "absolute",
                          bottom: 0,
                          right: 0,
                          "& .MuiSpeedDial-fab": {
                            width: { xs: 32, md: 36 },
                            height: { xs: 32, md: 36 },
                            minHeight: { xs: 32, md: 36 },
                            bgcolor: "transparent",
                            color: "#4f5d6b",
                            boxShadow: "none",
                            margin: 0,
                            "& .MuiSvgIcon-root": {
                              fontSize: { xs: 17, md: 18 },
                            },
                            "&:hover": {
                              bgcolor: alpha("#22364d", 0.06),
                              boxShadow: "none",
                            },
                          },
                        }}
                        FabProps={{
                          disabled:
                            sendMessageLoading || messagesLoading || !conversationId,
                        }}
                      >
                        <SpeedDialAction
                          icon={<InsertDriveFileOutlinedIcon />}
                          tooltipTitle="Attach File"
                          onClick={() => setUploadDialogOpen(true)}
                        />
                        <SpeedDialAction
                          icon={<PersonOutlinedIcon />}
                          tooltipTitle="Attach Creator"
                          onClick={() => setCreatorDialogOpen(true)}
                        />
                        <SpeedDialAction
                          icon={<CampaignOutlinedIcon />}
                          tooltipTitle="Attach Campaign"
                          onClick={() => setCampaignDialogOpen(true)}
                        />
                        <SpeedDialAction
                          icon={<LocalShippingIcon />}
                          tooltipTitle="Attach Shipping Address"
                          onClick={() => setCreatorShippingAddress(true)}
                        />
                        {metadata?.conversation?.campaign_id !== null && (
                          <SpeedDialAction
                            icon={<MovieCreationIcon />}
                            tooltipTitle="Edit Video Settings"
                            onClick={() => setOpenVideo(true)}
                          />
                        )}
                      </SpeedDial>
                    </Box>
                  ) : (
                    <Box sx={{ width: { xs: 32, md: 36 }, height: { xs: 32, md: 36 }, position: "relative" }}>
                      <SpeedDial
                        ariaLabel="Attachment Menu"
                        icon={<AttachFileIcon />}
                        direction="up"
                        sx={{
                          position: "absolute",
                          bottom: 0,
                          right: 0,
                          "& .MuiSpeedDial-fab": {
                            width: { xs: 32, md: 36 },
                            height: { xs: 32, md: 36 },
                            minHeight: { xs: 32, md: 36 },
                            bgcolor: "transparent",
                            color: "#4f5d6b",
                            boxShadow: "none",
                            margin: 0,
                            "& .MuiSvgIcon-root": {
                              fontSize: { xs: 17, md: 18 },
                            },
                            "&:hover": {
                              bgcolor: alpha("#22364d", 0.06),
                              boxShadow: "none",
                            },
                          },
                        }}
                        FabProps={{
                          disabled: sendMessageLoading || !allMessages.length,
                        }}
                      >
                        <SpeedDialAction
                          icon={<InsertDriveFileOutlinedIcon />}
                          tooltipTitle="Attach File"
                          onClick={() => setCreatorUploadDialogOpen(true)}
                        />
                        <SpeedDialAction
                          icon={<LocalShippingIcon />}
                          tooltipTitle="Attach Shipping Address"
                          onClick={() => setCreatorShippingAddress(true)}
                        />
                      </SpeedDial>
                    </Box>
                  )}

                  {!isCreator ? (
                    <IconButton
                      sx={{
                        width: { xs: 32, md: 36 },
                        height: { xs: 32, md: 36 },
                        color: "#27405f",
                        opacity: showCompactMoneyAction ? 1 : 0,
                        pointerEvents: showCompactMoneyAction ? "auto" : "none",
                        transition: "opacity 140ms ease",
                        "&:hover": {
                          bgcolor: alpha("#27405f", 0.1),
                        },
                      }}
                      aria-label="send offer"
                      onClick={() => setSendOfferOpen(true)}
                      disabled={sendMessageLoading || messagesLoading || !conversationId}
                    >
                      <MonetizationOnIcon sx={{ fontSize: { xs: 17, md: 18 } }} />
                    </IconButton>
                  ) : (
                    <IconButton
                      sx={{
                        width: { xs: 32, md: 36 },
                        height: { xs: 32, md: 36 },
                        color: "#27405f",
                        opacity: showCompactMoneyAction ? 1 : 0,
                        pointerEvents: showCompactMoneyAction ? "auto" : "none",
                        transition: "opacity 140ms ease",
                        "&:hover": {
                          bgcolor: alpha("#27405f", 0.1),
                        },
                      }}
                      aria-label="request invoice"
                      onClick={() => setInvoiceDialogOpen(true)}
                      disabled={sendMessageLoading || messagesLoading || !conversationId}
                    >
                      <MonetizationOnIcon sx={{ fontSize: { xs: 17, md: 18 } }} />
                    </IconButton>
                  )}

                  <IconButton
                    disabled={
                      sendMessageLoading || messagesLoading || !conversationId
                    }
                    onClick={handleSendMessage}
                    color="primary"
                    sx={{
                      width: { xs: 40, md: 42 },
                      height: { xs: 40, md: 42 },
                      bgcolor: "#27405f",
                      color: "#fff",
                      boxShadow: `0 10px 22px ${alpha("#27405f", 0.24)}`,
                      "&:hover": {
                        bgcolor: "#21354e",
                        filter: "brightness(0.96)",
                      },
                      "&:disabled": {
                        opacity: 0.5,
                        bgcolor: alpha(theme.palette.text.primary, 0.12),
                        color: theme.palette.text.disabled,
                      },
                    }}
                  >
                    {sendMessageLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <SendIcon sx={{ ml: 0.12, fontSize: { xs: 19, md: 18 } }} />
                    )}
                  </IconButton>
                </Box>
              </Box>
            ) : (
              <>
                {/* Attachment SpeedDial (for brand or for creator) */}
                {!isCreator && (
                  <Box sx={{ width: isPartnershipClean ? "44px" : "56px", height: isPartnershipClean ? "44px" : "56px", position: "relative", flexShrink: 0, mb: isPartnershipClean ? 0 : 0.25, ml: isPartnershipClean ? 1 : 0 }}>
                    <SpeedDial
                      ariaLabel="Attachment Menu"
                      icon={<AttachFileIcon />}
                      direction="up"
                      sx={{
                        position: "absolute", bottom: 0, right: 0,
                        ...(isPartnershipClean && {
                          "& .MuiSpeedDial-fab": {
                            width: 44,
                            height: 44,
                            minHeight: 44,
                            bgcolor: "transparent",
                            color: "text.secondary",
                            boxShadow: "none",
                            margin: 0,
                            "&:hover": {
                              bgcolor: "action.hover",
                              boxShadow: "none",
                            },
                          }
                        })
                      }}
                      FabProps={{
                        disabled:
                          sendMessageLoading || messagesLoading || !conversationId,
                      }}
                    >
                      <SpeedDialAction
                        icon={<InsertDriveFileOutlinedIcon />}
                        tooltipTitle="Attach File"
                        onClick={() => setUploadDialogOpen(true)}
                      />
                      <SpeedDialAction
                        icon={<PersonOutlinedIcon />}
                        tooltipTitle="Attach Creator"
                        onClick={() => setCreatorDialogOpen(true)}
                      />
                      <SpeedDialAction
                        icon={<CampaignOutlinedIcon />}
                        tooltipTitle="Attach Campaign"
                        onClick={() => setCampaignDialogOpen(true)}
                      />
                      <SpeedDialAction
                        icon={<LocalShippingIcon />}
                        tooltipTitle="Attach Shipping Address"
                        onClick={() => setCreatorShippingAddress(true)}
                      />
                      {metadata?.conversation?.campaign_id !== null && (
                        <SpeedDialAction
                          icon={<MovieCreationIcon />}
                          tooltipTitle="Edit Video Settings"
                          onClick={() => setOpenVideo(true)}
                        />
                      )}
                    </SpeedDial>
                  </Box>
                )}

                {isCreator && (
                  <Box sx={{ width: isPartnershipClean ? "44px" : "56px", height: isPartnershipClean ? "44px" : "56px", position: "relative", flexShrink: 0, mb: isPartnershipClean ? 0 : 0.25, ml: isPartnershipClean ? 1 : 0 }}>
                    <SpeedDial
                      ariaLabel="Attachment Menu"
                      icon={<AttachFileIcon />}
                      direction="up"
                      sx={{
                        position: "absolute", bottom: 0, right: 0,
                        ...(isPartnershipClean && {
                          "& .MuiSpeedDial-fab": {
                            width: 44,
                            height: 44,
                            minHeight: 44,
                            bgcolor: "transparent",
                            color: "text.secondary",
                            boxShadow: "none",
                            margin: 0,
                            "&:hover": {
                              bgcolor: "action.hover",
                              boxShadow: "none",
                            },
                          }
                        })
                      }}
                      FabProps={{
                        disabled: sendMessageLoading || !allMessages.length,
                      }}
                    >
                      <SpeedDialAction
                        icon={<InsertDriveFileOutlinedIcon />}
                        tooltipTitle="Attach File"
                        onClick={() => setCreatorUploadDialogOpen(true)}
                      />
                      <SpeedDialAction
                        icon={<LocalShippingIcon />}
                        tooltipTitle="Attach Shipping Address"
                        onClick={() => setCreatorShippingAddress(true)}
                      />
                    </SpeedDial>
                  </Box>
                )}

                {!isCreator && (
                  <Box sx={{ width: isPartnershipClean ? "44px" : "56px", height: isPartnershipClean ? "44px" : "56px", position: "relative", flexShrink: 0, mb: isPartnershipClean ? 0 : 0.25 }}>
                    {isPartnershipClean ? (
                      <IconButton
                        sx={{
                          position: "absolute", bottom: 0, right: 0,
                          width: 44,
                          height: 44,
                          bgcolor: "transparent",
                          color: "text.secondary",
                          "&:hover": {
                            bgcolor: "action.hover",
                          },
                        }}
                        aria-label="send offer"
                        onClick={() => setSendOfferOpen(true)}
                        disabled={sendMessageLoading || messagesLoading || !conversationId}
                      >
                        <MonetizationOnIcon />
                      </IconButton>
                    ) : (
                      <Fab
                        sx={{ position: "absolute", bottom: 0, right: 0 }}
                        color="primary"
                        aria-label="send offer"
                        onClick={() => setSendOfferOpen(true)}
                        disabled={sendMessageLoading || messagesLoading || !conversationId}
                      >
                        <MonetizationOnIcon />
                      </Fab>
                    )}
                  </Box>
                )}

                {isCreator && (
                  <Box sx={{ width: isPartnershipClean ? "44px" : "56px", height: isPartnershipClean ? "44px" : "56px", position: "relative", flexShrink: 0, mb: isPartnershipClean ? 0 : 0.25 }}>
                    {isPartnershipClean ? (
                      <IconButton
                        sx={{
                          position: "absolute", bottom: 0, right: 0,
                          width: 44,
                          height: 44,
                          bgcolor: "transparent",
                          color: "text.secondary",
                          "&:hover": {
                            bgcolor: "action.hover",
                          },
                        }}
                        aria-label="request invoice"
                        onClick={() => setInvoiceDialogOpen(true)}
                        disabled={sendMessageLoading || messagesLoading || !conversationId}
                      >
                        <MonetizationOnIcon />
                      </IconButton>
                    ) : (
                      <Fab
                        sx={{ position: "absolute", bottom: 0, right: 0 }}
                        color="secondary"
                        aria-label="request invoice"
                        onClick={() => setInvoiceDialogOpen(true)}
                        disabled={sendMessageLoading || messagesLoading || !conversationId}
                      >
                        <MonetizationOnIcon />
                      </Fab>
                    )}
                  </Box>
                )}

                <Box sx={{ flex: 1, minWidth: 0, position: "relative" }}>
                  <MentionSuggestions
                    isVisible={mentions.isVisible}
                    participants={mentions.filteredParticipants}
                    selectedIndex={mentions.selectedIndex}
                    onSelect={(p) => {
                      const newMsg = mentions.insertMention(p, message);
                      setMessage(newMsg);
                    }}
                    getDisplayName={mentions.getDisplayName}
                  />
                  <TextField
                    multiline
                    maxRows={MAX_MESSAGE_ROWS}
                    value={message}
                    autoComplete="off"
                    name="chat-message"
                    inputRef={mentions.inputRef}
                    disabled={
                      sendMessageLoading ||
                      messagesLoading ||
                      !conversationId ||
                      !!selectedAttachment
                    }
                    onChange={(e) => {
                      handleTyping(e.target.value);
                      mentions.onTextChange(
                        e.target.value,
                        e.target.selectionStart,
                      );
                    }}
                    label={isPartnershipClean ? undefined : "Type a message "}
                    placeholder={isPartnershipClean ? "Message..." : undefined}
                    variant="outlined"
                    fullWidth
                    onBlur={() =>
                      setTimeout(() => mentions.closeSuggestions(), 150)
                    }
                    onKeyDown={(e) => {
                      if (mentions.isVisible) {
                        if (e.key === "ArrowDown") {
                          e.preventDefault();
                          mentions.moveDown();
                          return;
                        }
                        if (e.key === "ArrowUp") {
                          e.preventDefault();
                          mentions.moveUp();
                          return;
                        }
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          const selected =
                            mentions.filteredParticipants[mentions.selectedIndex];
                          if (selected) {
                            setMessage(mentions.insertMention(selected, message));
                          }
                          return;
                        }
                        if (e.key === "Escape") {
                          e.preventDefault();
                          mentions.closeSuggestions();
                          return;
                        }
                      }
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    InputProps={{
                      ...(selectedAttachment && {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Chip
                              icon={getIcon(selectedAttachment.type)}
                              avatar={
                                selectedAttachment.preview ? (
                                  <Avatar
                                    alt={selectedAttachment.label}
                                    src={selectedAttachment.preview}
                                  />
                                ) : undefined
                              }
                              label={selectedAttachment.label}
                              sx={{ maxWidth: "14rem" }}
                              onDelete={() => setSelectedAttachment(null)}
                            />
                          </InputAdornment>
                        ),
                      }),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: isPartnershipClean ? 24 : 999,
                        bgcolor: theme.palette.background.paper,
                        alignItems: "center",
                        minHeight: isPartnershipClean ? 44 : undefined,
                        padding: isPartnershipClean ? "8px 16px" : undefined,
                        boxShadow: isPartnershipClean
                          ? `0 1px 2px ${alpha(theme.palette.common.black, 0.05)}`
                          : `0 1px 4px ${alpha(theme.palette.common.black, 0.08)}`,
                        "& fieldset": {
                          borderColor: alpha(theme.palette.divider, 0.7),
                        },
                        "&:hover fieldset": {
                          borderColor: alpha(conversationColor, 0.55),
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: conversationColor,
                        },
                      },
                      "& .MuiOutlinedInput-input": {
                        py: isPartnershipClean ? 0 : undefined,
                        fontSize: isPartnershipClean ? "0.95rem" : undefined,
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: conversationColor,
                      },
                    }}
                  />
                </Box>

                <Box sx={{ width: isPartnershipClean ? "48px" : "56px", height: isPartnershipClean ? "48px" : "56px", position: "relative", flexShrink: 0, mb: isPartnershipClean ? 0 : 0.25 }}>
                  <IconButton
                    disabled={
                      sendMessageLoading || messagesLoading || !conversationId
                    }
                    onClick={handleSendMessage}
                    color="primary"
                    sx={{
                      position: "absolute", bottom: 0, right: 0,
                      width: isPartnershipClean ? 48 : 56,
                      height: isPartnershipClean ? 48 : 56,
                      bgcolor: isPartnershipClean ? "#1e1e1e" : conversationColor,
                      color: "#fff",
                      boxShadow: isPartnershipClean ? "none" : `0 3px 10px ${alpha(conversationColor, 0.3)}`,
                      "&:hover": {
                        bgcolor: isPartnershipClean ? "#333333" : conversationColor,
                        filter: "brightness(0.94)",
                      },
                      "&:disabled": {
                        opacity: 0.5,
                        bgcolor: alpha(theme.palette.text.primary, 0.12),
                        color: theme.palette.text.disabled,
                      },
                    }}
                  >
                    {sendMessageLoading ? (
                      <CircularProgress size={24} />
                    ) : (
                      <SendIcon sx={{ ml: isPartnershipClean ? 0.3 : 0 }} />
                    )}
                  </IconButton>
                </Box>
              </>
            )}
          </Box>
        </Box>

        {/* Enhanced Snackbar */}
        <Snackbar
          open={showMessageCapAlert}
          autoHideDuration={SNACKBAR_DURATION}
          onClose={() => setShowMessageCapAlert(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setShowMessageCapAlert(false)}
            severity="warning"
            sx={{ width: "100%" }}
          >
            Messages since last creator response: You are capped at{" "}
            {MAX_MESSAGES_WITHOUT_RESPONSE}
          </Alert>
        </Snackbar>
      </Box>

      {/* All existing dialogs */}
      <EditVideoLinksDialog
        isOpen={openVideoLink}
        campaignId={metadata?.conversation?.campaign_id}
        creatorId={metadata?.conversation?.creator_id}
        videoId={videoLinkId}
        isUser={!isCreator}
        conversationId={conversationId}
        onClose={() => {
          setOpenVideoLink(false);
          messagesRefetch();
        }}
      />

      <CampaignVideoEditor
        isOpen={openVideo}
        campaignId={metadata?.conversation?.campaign_id}
        creator={metadata?.conversation?.creator_id}
        onClose={() => {
          setOpenVideo(false);
          messagesRefetch();
        }}
      />

      <SendOfferDialog
        open={sendOfferOpen}
        onClose={() => setSendOfferOpen(false)}
        partnershipId={partnershipId}
        creatorId={creatorId}
        creator={
          creator ||
          (creatorId ? { creator: creatorId, display_name: creatorId } : null)
        }
        conversationId={conversationId}
        onOfferSent={handleOfferSent}
      />

      <SelectAssetDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        selectedAsset={selectedAttachment}
        onAssetClick={handleFileSelect}
      />

      <SelectCreatorDialog
        open={creatorDialogOpen}
        onClose={() => setCreatorDialogOpen(false)}
        onCreatorSelect={handleCreatorSelect}
      />

      <SelectCampaignDialog
        open={campaignDialogOpen}
        onClose={() => setCampaignDialogOpen(false)}
        onCampaignSelect={handleCampaignSelect}
      />

      <CreatorUploadFileDialog
        open={creatorUploadDialogOpen}
        onClose={() => setCreatorUploadDialogOpen(false)}
        conversationId={conversationId}
        readerId={specificReaderId}
        onFileUpload={handleCreatorFile}
        onError={handleCreatorUploadError}
      />

      <ShippingAddressDialog
        open={creatorShippingAddress}
        onClose={handleShippingSelect}
      />

      <ReadReceipt
        open={currentMessage !== null}
        handleClose={() => setCurrentMessage(null)}
        message={currentMessage}
        participants={conversation?.participants}
        currentUsername={userId}
      />

      <AlertDialog alertState={dialogState} />

      <OfferDialog
        open={viewOfferOpen}
        onClose={() => setViewOfferOpen(false)}
        offer={selectedOffer}
        isCreator={isCreator}
        onOfferUpdated={(updatedOffer) => {
          setSelectedOffer(updatedOffer);
        }}
      />

      <PdfSigningDialog
        open={pdfSigningDialogOpen}
        onClose={() => setPdfSigningDialogOpen(false)}
        fileData={fileForSigning}
        onFileSigned={(updatedFile) => {
          sendMessage({
            id: conversationId,
            input: {
              message: "",
              attachment: {
                type: "file",
                data: updatedFile,
              },
            },
          });
          setPdfSigningDialogOpen(false);
        }}
      />

      <CreatorInvoiceDialog
        open={invoiceDialogOpen}
        onClose={() => setInvoiceDialogOpen(false)}
        onInvoiceCreated={handleInvoiceCreated}
      />

      <Dialog
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Deliverable Workflow</DialogTitle>
        <DialogContent dividers>
          <List>
            <ProcessStep text="1. Send creator offer" />
            <ProcessStep text="2. Creator accepts or negotiates" />
            <ProcessStep text="3. Upon completion, user completes the offer" />
            <ProcessStep text="4. Creator is paid, user is invoiced" />
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpOpen(false)} autoFocus>
            Got it
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.msg !== ""}
        autoHideDuration={3000}
        onClose={() => setSnack({ msg: "", sev: "info" })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnack({ msg: "", sev: "info" })}
          severity={snack.sev}
          sx={{ width: "100%" }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
});

export default ChatScreen;
